import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { QuotaExceededInfo, QUOTA_DEGRADED_HEADER } from 'app/shared/utils/quota.utils';
import { BehaviorSubject, Observable } from 'rxjs';

/** Header the backend sets on every quota-evaluated response, degraded or not. */
const QUOTA_LIMIT_HEADER = 'X-Quota-Limit';

/**
 * App-wide state for what the quota middleware last told us.
 *
 * Root-provided so the interceptor (which cannot render) and the shell banner
 * (which cannot see HTTP) share one truth, the same split `OrgMismatchState`
 * already uses for cross-tenant 403s.
 */
@Injectable({ providedIn: 'root' })
export class QuotaNoticeService {
    private readonly _denial$ = new BehaviorSubject<QuotaExceededInfo | null>(
        null
    );
    private readonly _degraded$ = new BehaviorSubject<boolean>(false);

    /** The most recent quota denial, or `null` once acknowledged. */
    get denial$(): Observable<QuotaExceededInfo | null> {
        return this._denial$.asObservable();
    }

    /** True while quota enforcement is running unmeasured (D3). */
    get degraded$(): Observable<boolean> {
        return this._degraded$.asObservable();
    }

    /** D2: a 429/503 quota verdict arrived. Last one wins — it is the current reality. */
    reportDenial(info: QuotaExceededInfo): void {
        this._denial$.next(info);
    }

    /**
     * Dismiss the denial banner. Called when the user navigates away: the message is
     * about the action they just attempted, so it must not follow them around the app.
     */
    clearDenial(): void {
        if (this._denial$.value !== null) this._denial$.next(null);
    }

    /**
     * D3: track the fail-open flag off any quota-evaluated response.
     *
     * `X-Quota-Degraded` means quota-service was unreachable and the request was let
     * through WITHOUT being counted (TODO B3c). Nothing in the app read it, so a user
     * could run unmetered with no indication. Recovery is inferred from the same
     * headers: a response that WAS evaluated (`X-Quota-Limit` present) and is not
     * flagged degraded proves the limiter is answering again. Responses with no quota
     * headers at all say nothing either way and must leave the state alone.
     */
    observeHeaders(headers: HttpHeaders): void {
        if (headers.has(QUOTA_DEGRADED_HEADER)) {
            if (!this._degraded$.value) this._degraded$.next(true);
            return;
        }
        if (headers.has(QUOTA_LIMIT_HEADER) && this._degraded$.value) {
            this._degraded$.next(false);
        }
    }
}
